import { setFieldError } from "@/stores";
import { usePasswordValidation } from "@/hooks";
import { createPassword } from "../_helpers";
import { useForm } from "@/hooks";
import { useLocale } from "@/i18n";
import { useSearchParams } from "react-router-dom";

/**
 * useCreatePasswordForm Custom Hook
 *
 * Manages password creation form state and submission logic.
 * Handles validation, API calls, error handling, and navigation.
 */
export const useCreatePasswordForm = () => {
  const locale = useLocale();
  const [searchParams] = useSearchParams();
  const nextParam = searchParams.get("next");
  // Get form values for password validation
  const { values } = useForm();

  // Validate password requirements at hook level
  const { isPasswordValid } = usePasswordValidation(
    String(values.password ?? "")
  );

  /**
   * Handles input change
   * Updates field value and clears any existing errors
   */
  const handleInputChange = (field: string, value: string) => {
    // This will be handled by the parent component's useForm hook
    // We just need to clear errors when user types
    setFieldError(field, "");
  };

  /**
   * Handles form submission
   * Validates password requirements and submits for registration
   */
  const handleFormSubmit = async (values: any) => {
    const { password, repeatPassword } = values;

    // Validate repeat password
    const emptyRepeatPasswordError = repeatPassword.length === 0;
    const diffRepeatPasswordError = password !== repeatPassword;

    // Set validation errors
    setFieldError(
      "password",
      !isPasswordValid ? "Password does not meet requirements" : ""
    );
    setFieldError(
      "repeatPassword",
      emptyRepeatPasswordError
        ? "Passwords do not match"
        : diffRepeatPasswordError
        ? "Passwords do not match"
        : ""
    );

    // If validation fails, stop here
    if (
      !isPasswordValid ||
      emptyRepeatPasswordError ||
      diffRepeatPasswordError
    ) {
      return;
    }

    await createPassword(values, { locale, next: nextParam });
  };

  return {
    handleInputChange,
    handleFormSubmit,
  };
};
